import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";

type AppRole = "admin" | "restaurant" | "guest" | "menu_manager";

interface UserWithRole {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
}

export const useAuth = (): UserWithRole => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleInvalidJwt = async (error: unknown) => {
      const msg = (error as { message?: string } | null)?.message?.toLowerCase() ?? "";
      if (
        msg.includes("missing sub claim") ||
        msg.includes("bad_jwt") ||
        msg.includes("invalid claim") ||
        msg.includes("jwt expired")
      ) {
        console.warn("[useAuth] Invalid JWT detected, signing out:", msg);
        try {
          await supabase.auth.signOut();
        } catch (e) {
          // ignore
        }
        // Clear any stale tokens from localStorage
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith("sb-") && k.includes("-auth-token"))
            .forEach((k) => localStorage.removeItem(k));
        } catch (e) {
          // ignore
        }
        setSession(null);
        setUser(null);
        setRole(null);
        setLoading(false);
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
        return true;
      }
      return false;
    };

    // Listen for auth changes FIRST (so we don't miss events)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setRole(null);
        setLoading(false);
      }
    });

    // Then check existing session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error && (await handleInvalidJwt(error))) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Verify the user is still valid server-side
        const { error: userError } = await supabase.auth.getUser();
        if (userError && (await handleInvalidJwt(userError))) return;
        fetchUserRole(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .single();

      if (error) {
        console.error("Error fetching user role:", error);
        setRole(null);
      } else {
        setRole(data?.role as AppRole);
      }
    } catch (error) {
      console.error("Error in fetchUserRole:", error);
      setRole(null);
    } finally {
      setLoading(false);
    }
  };

  return { user, session, role, loading };
};
